import * as React from 'react';
import {createViewConfig} from '@sail/engine';
import type {View} from '../view';
import {Input} from './Input';
import type {FormFieldProps} from './FormField';
import {useFormFieldProps, FormField} from './FormField';
import {tokens} from '../tokens';
import {baseline} from '../css';

export type NativeSwitchProps = View.IntrinsicElement<
  'input',
  {
    /**
     * Sets whether or not the element is in an invalid state. This is a display-only prop, and will not prevent form submission.
     * @external
     */
    invalid?: boolean;
  }
>;

const switchSize = '18px';
const switchTrackWidth = `calc((${switchSize} * 2) - 2px)`;

export const NativeSwitchConfig = createViewConfig({
  props: {} as NativeSwitchProps,
  name: 'NativeSwitch',
});

export const NativeSwitch = NativeSwitchConfig.createView(
  (props) => <Input type="checkbox" role="switch" {...props} />,
  {
    uses: [baseline.css('cap-middle object-middle')],
    css: {
      appearance: 'none',
      display: 'inline',
      stack: 'z',
      width: switchTrackWidth,
      height: switchSize,
      backgroundColor: tokens.color.neutral[50],
      borderRadius: 'rounded',
      cursor: 'pointer',
      transition: '0.2s',
      keyline: 'form',
      topShadow: 'form',
      margin: 'space.0',
      position: 'relative',
      ':focus': {
        focusRing: 'focus',
      },
      '::after': {
        transition: '0.2s',
        width: switchSize,
        height: switchSize,
        display: 'block',
        backgroundColor: 'form',
        keyline: 'form',
        topShadow: 'form',
        borderRadius: 'rounded',
        content: '""',
      },

      // Checked
      ':checked': {
        backgroundColor: 'form.accent',
        keyline: 'form.accent',
      },
      ':checked:after': {
        marginLeft: `calc(${switchTrackWidth} - ${switchSize})`,
      },
      ':checked:not(:disabled):after': {
        keyline: 'form.accent',
      },

      // Disabled
      ':disabled': {
        cursor: 'default',
        backgroundColor: 'form.disabled',
        keyline: 'form.disabled',
        topShadow: 'transparent 0 0 0 0',
      },
      ':disabled::after': {
        backgroundColor: 'form.disabled',
        topShadow: 'transparent 0 0 0 0',
      },
      ':checked:disabled': {
        backgroundColor: 'form.accent.disabled',
        keyline: 'form.accent.disabled',
        topShadow: 'transparent 0 0 0 0',
      },
      ':checked:disabled::after': {
        backgroundColor: 'onForm.accent.disabled',
        keyline: 'onForm.accent.disabled',
      },
      // Hovered
      ':hover:not(:disabled):not(:active)': {
        keyline: 'form.hovered',
        topShadow: 'form.hovered',
      },
      ':hover:not(:disabled):not(:active):after': {
        keyline: 'form.hovered',
        topShadow: 'form.hovered',
      },

      // Checked + hovered
      ':checked:hover:not(:disabled):not(:active)': {
        keyline: 'form.accent.hovered',
        topShadow: 'form.accent.hovered',
      },
      ':checked:hover:not(:disabled):not(:active):after': {
        keyline: 'form.accent.hovered',
        topShadow: 'form.accent.hovered',
      },
      // Active
      ':active:not(:disabled)': {
        keyline: 'form.pressed',
        topShadow: 'form.pressed',
      },
      ':active:not(:disabled):after': {
        keyline: 'form.pressed',
        topShadow: 'form.pressed',
        backgroundColor: tokens.color.neutral[25],
      },

      // Checked + active
      ':checked:active:not(:disabled)': {
        keyline: 'form.accent.pressed',
        topShadow: 'form.accent.pressed',
      },
      ':checked:active:not(:disabled):after': {
        keyline: 'form.accent.pressed',
        topShadow: 'form.accent.pressed',
      },

      // Error
      '[aria-invalid="true"]': {
        keyline: 'form.error',
        topShadow: 'form.error',
      },
      '[aria-invalid="true"]:after': {
        keyline: 'form.error',
        topShadow: 'form.error',
      },
      '[aria-invalid="true"]:checked:not(:disabled)': {
        keyline: 'form.error',
        topShadow: 'form.error',
      },
      '[aria-invalid="true"]:checked:not(:disabled):after': {
        keyline: 'form.error',
        topShadow: 'form.error',
      },
    },
  },
);

export const SwitchConfig = createViewConfig({
  props: {} as FormFieldProps<NativeSwitchProps>,
  name: 'Switch',
  flattens: true,
});

/**
 * @external
 * @param autoFocus
 * @param defaultChecked
 * @param disabled
 * @param form
 * @param invalid
 * @param name
 * @param onChange
 * @param readOnly
 * @param required
 * @param tabIndex
 * @param value
 */
export const Switch = SwitchConfig.createView((props) => {
  const [fieldProps, controlProps] = useFormFieldProps(props);
  return (
    <FormField {...fieldProps} layout="horizontal">
      <NativeSwitch {...controlProps} inherits={props.subviews.input} />
    </FormField>
  );
});
